import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

/**
 * Returns the canonical 10-digit US phone number for the given input, or
 * null when the input cannot be parsed as a valid 10/11-digit US number.
 */
export function normalizeUsPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = String(raw).replace(/\D+/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return ten.length === 10 ? ten : null;
}

export function isValidUsPhone(raw: string | null | undefined): boolean {
  return normalizeUsPhone(raw) !== null;
}

export function formatUsPhone(raw: string | null | undefined): string {
  const ten = normalizeUsPhone(raw);
  if (!ten) return '';
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

// ─── Dial codes (SCRUM-1833) ────────────────────────────────────────────────
//
// A phone's country is its DIAL CODE — a digit string such as '44' — never a
// countryID. Blank, missing or '0' means '1'. Numbers are stored exactly as
// typed (digits only, leading zeros kept); everything below only formats.

/** Fewest and most digits a phone number can plausibly have. */
export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

/** The digits of any value, '' for nothing. */
export function phoneDigits(raw: string | number | null | undefined): string {
  return String(raw ?? '').replace(/\D+/g, '');
}

/** The dial code: the digits of the stored value. Blank, missing or '0' is '1'. */
export function normalizeDialCode(code: string | number | null | undefined): string {
  const digits = phoneDigits(code);
  return digits !== '' && digits !== '0' ? digits : '1';
}

/** 7–15 digits. The only check a phone gets; it is not tied to any one country. */
export function isPlausiblePhone(raw: string | null | undefined): boolean {
  const count = phoneDigits(raw).length;
  return count >= PHONE_MIN_DIGITS && count <= PHONE_MAX_DIGITS;
}

function usMask(ten: string): string {
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * A phone for display, with its dial code in front.
 *
 *  - Code '1' keeps the US look: `+1 (555) 123-4567`.
 *  - Other codes use libphonenumber's international format: `+44 7911 123456`.
 *  - Anything the library cannot format is `+<code> <digits>`.
 *
 * '' when there are no digits.
 */
export function formatPhoneByCode(
  raw: string | null | undefined,
  countryCode: string | null | undefined = '1',
): string {
  const digits = phoneDigits(raw);
  if (!digits) return '';
  const code = normalizeDialCode(countryCode);

  if (code === '1') {
    if (digits.length === 10) return `+1 ${usMask(digits)}`;
    if (digits.length === 11 && digits[0] === '1') return `+1 ${usMask(digits.slice(1))}`;
    return `+1 ${digits}`;
  }

  const parsed = parsePhoneNumberFromString(`+${code}${digits}`);
  if (parsed?.isValid()) return parsed.formatInternational();
  return `+${code} ${digits}`;
}

/**
 * The number as it is being typed, for an input that sits beside a dial-code
 * picker — so the code itself is not repeated. Code '1' keeps today's
 * `(555) 123-4567` mask (digits past ten trail after a space); other codes
 * group the way libphonenumber does. Leading zeros are shown as typed.
 */
export function formatPhoneAsYouType(
  raw: string | null | undefined,
  countryCode: string | null | undefined = '1',
): string {
  const digits = phoneDigits(raw);
  if (!digits) return '';
  const code = normalizeDialCode(countryCode);

  if (code === '1') {
    const d = digits.slice(0, 10);
    const extra = digits.slice(10);
    let formatted: string;
    if (d.length <= 3) formatted = `(${d}`;
    else if (d.length <= 6) formatted = `(${d.slice(0, 3)}) ${d.slice(3)}`;
    else formatted = usMask(d);
    return extra ? `${formatted} ${extra}` : formatted;
  }

  // Typed with the code in front so the library groups the national part the
  // international way, then the code is taken back off. The library may split
  // the code itself ('+1 242 …' for '1242'), so the code's digits are consumed
  // one by one rather than sliced off as a string.
  const formatted = new AsYouType().input(`+${code}${digits}`);
  let i = formatted.startsWith('+') ? 1 : 0;
  let matched = 0;
  while (i < formatted.length && matched < code.length) {
    const ch = formatted[i];
    if (/\d/.test(ch)) {
      if (ch !== code[matched]) return digits;
      matched += 1;
    }
    i += 1;
  }
  const national = formatted.slice(i).trim();
  return phoneDigits(national) === digits ? national : digits;
}
